<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Head, Link, usePage } from '@inertiajs/vue3';
import { useJsonLdMultiple } from '@/composables/useJsonLd';
import { register } from '@/routes';

withDefaults(defineProps<{ canRegister: boolean }>(), { canRegister: true });

const { t, tm, rt, locale } = useI18n();
const page = usePage<{ localeDomains: Record<string, string> }>();

const localeDomains = computed(() => page.props.localeDomains || {});

const canonicalUrl = computed(() => {
    return localeDomains.value[locale.value] || 'https://novantt.com';
});

const alternateLocales = computed(() => {
    return Object.entries(localeDomains.value)
        .filter(([loc]) => loc !== locale.value)
        .map(([loc, domain]) => ({
            locale: loc === 'nl' ? 'nl_NL' : loc === 'de' ? 'de_DE' : 'en_US',
            url: domain as string,
        }));
});

const faqJsonLd = computed(() => {
    const items = tm('welcome.faq') as { q: string; a: string }[];
    return JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': items.map(f => ({
            '@type': 'Question',
            'name': rt(f.q),
            'acceptedAnswer': { '@type': 'Answer', 'text': rt(f.a) },
        })),
    });
});

const orgJsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Novantt',
    'legalName': 'Novantt',
    'url': 'https://novantt.com',
    'logo': 'https://novantt.com/icon-512.png',
    'sameAs': [],
    'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'Stationsplein 45',
        'addressLocality': 'Rotterdam',
        'postalCode': '3013 AK',
        'addressRegion': 'Zuid-Holland',
        'addressCountry': 'NL',
    },
    'foundingDate': '2025',
    'numberOfEmployees': { '@type': 'QuantitativeValue', 'value': 5 },
    'areaServed': ['NL', 'BE', 'DE'],
    'knowsLanguage': ['nl', 'en', 'de'],
});

const websiteJsonLd = computed(() => JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'Novantt',
    'url': canonicalUrl.value,
    'inLanguage': locale.value,
    'description': t('welcome.metaDescription'),
    'publisher': { '@type': 'Organization', 'name': 'Novantt' },
}));

useJsonLdMultiple([
    { data: () => orgJsonLd, id: 'org' },
    { data: websiteJsonLd, id: 'website' },
    { data: faqJsonLd, id: 'faq' },
]);
</script>

<template>
    <Head :title="t('welcome.headTitle')">
        <meta name="description" :content="t('welcome.metaDescription')" />
        <meta name="keywords" :content="t('welcome.metaKeywords')" />
        <link rel="canonical" :href="canonicalUrl" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Novantt" />
        <meta property="og:title" :content="t('welcome.ogTitle')" />
        <meta property="og:description" :content="t('welcome.ogDescription')" />
        <meta property="og:url" :content="canonicalUrl" />
        <meta property="og:image" :content="canonicalUrl + '/og-image.webp'" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" :content="locale === 'nl' ? 'nl_NL' : locale === 'de' ? 'de_DE' : 'en_US'" />
        <meta v-for="alt in alternateLocales" :key="alt.locale" property="og:locale:alternate" :content="alt.locale" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@novantt" />
        <meta name="twitter:title" :content="t('welcome.ogTitle')" />
        <meta name="twitter:description" :content="t('welcome.ogDescription')" />
        <meta name="twitter:image" :content="canonicalUrl + '/og-image.webp'" />
        <link rel="preload" as="image" type="image/webp" imagesrcset="/images/homepage/hero-450.webp 450w, /images/homepage/hero.webp 900w" imagesizes="(max-width: 1023px) calc(100vw - 40px), 40vw" />
    </Head>

    <div class="nv-home font-dm">

        <!-- Hero -->
        <section aria-label="Hero" class="relative">
            <div class="mx-auto max-w-[1101px] px-5 pt-12 pb-14 md:px-6 md:pt-20 md:pb-24 lg:pt-24 lg:pb-28">
                <div class="grid grid-cols-12 gap-6 md:gap-8 items-start">
                    <div class="col-span-12 lg:col-span-7 pt-4 lg:pt-8">
                        <div class="hp-eyebrow mb-4 md:mb-5">{{ t('welcome.heroEyebrow') }}</div>
                        <h1 class="font-fraunces font-bold text-[34px] md:text-[48px] lg:text-[60px] leading-[1.05] tracking-[-0.025em] text-[color:var(--hp-ink)]">
                            {{ t('welcome.heroTitle') }} <em class="not-italic text-[color:var(--hp-accent)]">{{ t('welcome.heroTitleAccent') }}</em>
                        </h1>
                        <p class="mt-5 md:mt-6 max-w-[520px] text-[15px] md:text-base leading-7 text-[color:var(--hp-muted)]">
                            {{ t('welcome.heroSub') }}
                        </p>
                        <div class="mt-6 md:mt-8 flex flex-wrap items-center gap-4 md:gap-6">
                            <Link v-if="canRegister" :href="register()" class="rounded-full bg-[color:var(--hp-ink)] text-[color:var(--hp-cream)] px-5 md:px-6 h-11 md:h-12 inline-flex items-center text-sm font-medium hp-arrow">
                                {{ t('welcome.heroCta') }}
                            </Link>
                            <a href="#how" class="text-sm text-[color:var(--hp-muted)] underline underline-offset-4 hover:text-[color:var(--hp-ink)] py-2">{{ t('welcome.heroLink') }}</a>
                        </div>
                        <ul class="mt-8 md:mt-10 flex flex-wrap gap-x-6 md:gap-x-8 gap-y-2 text-sm text-[color:var(--hp-muted)] list-none p-0">
                            <li class="flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-[color:var(--hp-accent)]" aria-hidden="true"></span>{{ t('welcome.heroBullet1') }}</li>
                            <li class="flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-[color:var(--hp-accent)]" aria-hidden="true"></span>{{ t('welcome.heroBullet2') }}</li>
                            <li class="flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-[color:var(--hp-accent)]" aria-hidden="true"></span>{{ t('welcome.heroBullet3') }}</li>
                        </ul>
                    </div>

                    <div class="col-span-12 lg:col-span-5">
                        <div class="aspect-[16/10] lg:aspect-[3/4] w-full rounded-2xl overflow-hidden bg-[color:var(--hp-sand)]">
                            <picture>
                                <source type="image/webp" srcset="/images/homepage/hero-450.webp 450w, /images/homepage/hero.webp 900w" sizes="(max-width: 1023px) calc(100vw - 40px), 40vw" />
                                <img src="/images/homepage/hero.jpg" :alt="t('welcome.heroImgAlt')" width="900" height="1100" fetchpriority="high" class="w-full h-full object-cover" />
                            </picture>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Logo bar -->
        <section aria-label="Payment providers" class="border-y">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-6 md:py-7 text-center">
                <div class="text-[11px] md:text-[12px] tracking-[0.1em] text-[color:var(--hp-muted)]">{{ t('welcome.logoBar') }}</div>
                <div class="mt-3 flex items-center justify-center gap-8 md:gap-12">
                    <span class="font-fraunces font-bold text-[20px] md:text-[24px] text-[color:var(--hp-partner)]">Mollie</span>
                    <span class="w-px h-6 bg-[color:var(--hp-line)]" aria-hidden="true"></span>
                    <span class="font-fraunces font-bold text-[20px] md:text-[24px] text-[color:var(--hp-partner)]">Stripe</span>
                </div>
            </div>
        </section>

        <!-- Hoe het werkt -->
        <section id="how" class="border-b">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-14 md:py-20 lg:py-24">
                <div class="grid grid-cols-12 gap-8 md:gap-10">
                    <div class="col-span-12 lg:col-span-6">
                        <div class="hp-eyebrow">{{ t('welcome.howEyebrow') }}</div>
                        <h2 class="font-fraunces font-bold text-[28px] md:text-[36px] lg:text-[48px] leading-[1.1] mt-4 max-w-[480px]">{{ t('welcome.howTitle') }}</h2>
                        <div class="mt-8 md:mt-10 rounded-2xl overflow-hidden bg-[color:var(--hp-sand)] aspect-[16/10]">
                            <picture>
                                <source type="image/webp" srcset="/images/homepage/how-400.webp 400w, /images/homepage/how.webp 700w" sizes="(max-width: 1023px) calc(100vw - 40px), 50vw" />
                                <img src="/images/homepage/how.jpg" :alt="t('welcome.howImgAlt')" width="700" height="500" loading="lazy" class="w-full h-full object-cover" />
                            </picture>
                        </div>
                    </div>
                    <div class="col-span-12 lg:col-span-6 lg:pl-6">
                        <ol class="space-y-8 md:space-y-10">
                            <li v-for="(step, i) in (tm('welcome.steps') as any[])" :key="i" class="flex gap-4 md:gap-6">
                                <div class="font-fraunces font-bold text-[32px] md:text-[40px] leading-none text-[color:var(--hp-accent)] w-10 md:w-12 shrink-0">{{ i + 1 }}</div>
                                <div>
                                    <h3 class="text-[17px] md:text-[18px] font-medium text-[color:var(--hp-ink)]">{{ rt(step.t) }}</h3>
                                    <p class="mt-2 text-[color:var(--hp-muted)] leading-7 max-w-[420px]">{{ rt(step.d) }}</p>
                                </div>
                            </li>
                        </ol>
                        <div class="mt-10 md:mt-12 rounded-2xl border bg-[color:var(--hp-card)] p-5 md:p-6">
                            <div class="flex items-center gap-3">
                                <span class="w-2 h-2 rounded-full bg-[color:var(--hp-accent)]" aria-hidden="true"></span>
                                <span class="text-[12px] tracking-[0.1em] text-[color:var(--hp-muted)]">{{ t('welcome.liveTimeLabel') }}</span>
                            </div>
                            <div class="mt-3 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                                <div>
                                    <div class="text-sm text-[color:var(--hp-muted)]">{{ t('welcome.liveSub') }}</div>
                                    <div class="font-fraunces font-bold text-[20px] md:text-[22px] mt-1">{{ t('welcome.liveAmount') }}</div>
                                </div>
                                <div class="text-xs text-[color:var(--hp-muted)]">{{ t('welcome.liveFeeLabel') }} <span class="text-[color:var(--hp-ink)]">{{ t('welcome.liveFeeAmount') }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Dark statement -->
        <section aria-label="Statement" class="bg-[color:var(--hp-statement-bg)] text-[color:var(--hp-statement-fg)]">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-16 md:py-24 lg:py-28 text-center">
                <h2 class="font-fraunces font-bold text-[28px] md:text-[36px] lg:text-[48px] leading-[1.15] max-w-[820px] mx-auto">
                    {{ t('welcome.statementTitle') }}
                    <span class="text-[color:var(--hp-accent)]">{{ t('welcome.statementAccent') }}</span>
                </h2>
                <p class="mt-5 md:mt-7 text-[15px] md:text-base text-[color:var(--hp-statement-fg)] opacity-55 max-w-[560px] mx-auto">
                    {{ t('welcome.statementSub') }}
                </p>
            </div>
        </section>

        <!-- Features -->
        <section id="features" class="border-b">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-14 md:py-20 lg:py-24">
                <div class="grid grid-cols-12 gap-8 md:gap-10">
                    <div class="col-span-12 lg:col-span-6">
                        <div class="rounded-2xl overflow-hidden bg-[color:var(--hp-sand)] aspect-[16/10] lg:aspect-[4/5]">
                            <picture>
                                <source type="image/webp" srcset="/images/homepage/features-400.webp 400w, /images/homepage/features.webp 600w" sizes="(max-width: 1023px) calc(100vw - 40px), 50vw" />
                                <img src="/images/homepage/features.jpg" :alt="t('welcome.featuresImgAlt')" width="600" height="700" loading="lazy" class="w-full h-full object-cover" />
                            </picture>
                        </div>
                    </div>
                    <div class="col-span-12 lg:col-span-6 lg:pl-6 lg:pt-12">
                        <div class="hp-eyebrow">{{ t('welcome.featuresEyebrow') }}</div>
                        <h2 class="font-fraunces font-bold text-[24px] md:text-[30px] lg:text-[36px] leading-[1.15] mt-4 max-w-[420px]">{{ t('welcome.featuresTitle') }}</h2>
                        <div class="mt-8 md:mt-10 space-y-6 md:space-y-7">
                            <div v-for="(feat, i) in (tm('welcome.features') as any[])" :key="i" class="flex gap-4 md:gap-5">
                                <div class="w-10 h-10 rounded-xl bg-[color:var(--hp-card)] border shrink-0 flex items-center justify-center text-[color:var(--hp-accent)]">
                                    <svg v-if="i === 0" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z"/></svg>
                                    <svg v-else-if="i === 1" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
                                    <svg v-else-if="i === 2" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="4"/><path d="M3 21a6 6 0 0 1 12 0"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21a6 6 0 0 0-4-5.66"/></svg>
                                    <svg v-else aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="m7 14 4-4 3 3 5-6"/></svg>
                                </div>
                                <div>
                                    <h3 class="text-[15px] font-medium text-[color:var(--hp-ink)]">{{ rt(feat.t) }}</h3>
                                    <p class="mt-1 text-sm leading-6 text-[color:var(--hp-muted)] max-w-[430px]">{{ rt(feat.d) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Email + Automation -->
        <section aria-label="Email" class="border-b">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-14 md:py-20 lg:py-24">
                <div class="grid grid-cols-12 gap-8 md:gap-10 items-start">
                    <!-- Left: mock email composer + automation -->
                    <div class="col-span-12 lg:col-span-6 space-y-4 order-2 lg:order-1">
                        <div class="rounded-2xl bg-[color:var(--hp-card)] border shadow-[0_1px_2px_-1px_rgba(0,0,0,.10),0_1px_3px_0_rgba(0,0,0,.10)] overflow-hidden">
                            <div class="flex items-center justify-between bg-[color:var(--hp-card-alt)] border-b px-4 md:px-5 py-3">
                                <div class="flex gap-1.5">
                                    <span class="w-2.5 h-2.5 rounded-full bg-[color:var(--hp-accent)] opacity-70"></span>
                                    <span class="w-2.5 h-2.5 rounded-full bg-[color:var(--hp-accent)] opacity-40"></span>
                                    <span class="w-2.5 h-2.5 rounded-full bg-[color:var(--hp-accent)] opacity-20"></span>
                                </div>
                                <div class="text-xs text-[color:var(--hp-muted)]">{{ t('welcome.emailMockLabel') }}</div>
                            </div>
                            <div class="px-4 md:px-5 py-4 md:py-5 space-y-3">
                                <div class="flex items-center gap-3 border-b pb-3">
                                    <div class="text-xs text-[color:var(--hp-muted)] w-14">{{ t('welcome.emailMockFrom') }}</div>
                                    <div class="text-sm font-medium text-[color:var(--hp-ink)]">{{ t('welcome.emailMockSender') }}</div>
                                </div>
                                <div class="flex items-center gap-3 border-b pb-3">
                                    <div class="text-xs text-[color:var(--hp-muted)] w-14">{{ t('welcome.emailMockTo') }}</div>
                                    <div class="flex gap-2 flex-wrap">
                                        <span class="text-xs rounded-full bg-[color:var(--hp-sand)] px-2 py-0.5">{{ t('welcome.emailMockTag1') }}</span>
                                        <span class="text-xs rounded-full bg-[color:var(--hp-sand)] px-2 py-0.5">{{ t('welcome.emailMockTag2') }}</span>
                                    </div>
                                </div>
                                <div class="flex items-center gap-3 border-b pb-3">
                                    <div class="text-xs text-[color:var(--hp-muted)] w-14">{{ t('welcome.emailMockSubjectLabel') }}</div>
                                    <div class="text-sm font-medium text-[color:var(--hp-ink)] truncate">{{ t('welcome.emailMockSubject') }}</div>
                                </div>
                                <div class="text-sm text-[color:var(--hp-muted)] leading-6 whitespace-pre-line pt-1">{{ t('welcome.emailMockBody') }}</div>
                                <div class="flex items-center justify-between pt-2">
                                    <div class="text-xs text-[color:var(--hp-muted)]">{{ t('welcome.emailMockRecipients') }}</div>
                                    <span class="rounded-full bg-[color:var(--hp-btn-send)] text-white text-xs font-medium px-3 md:px-4 py-2" aria-hidden="true">{{ t('welcome.emailMockSend') }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Stats row -->
                        <div class="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <div class="font-fraunces font-bold text-[24px] md:text-[28px] text-[color:var(--hp-ink)] leading-none">68%</div>
                                <div class="text-xs text-[color:var(--hp-muted)] mt-2">{{ t('welcome.emailStatOpened') }}</div>
                            </div>
                            <div>
                                <div class="font-fraunces font-bold text-[24px] md:text-[28px] text-[color:var(--hp-ink)] leading-none">24%</div>
                                <div class="text-xs text-[color:var(--hp-muted)] mt-2">{{ t('welcome.emailStatClicked') }}</div>
                            </div>
                            <div>
                                <div class="font-fraunces font-bold text-[24px] md:text-[28px] text-[color:var(--hp-ink)] leading-none">0.3%</div>
                                <div class="text-xs text-[color:var(--hp-muted)] mt-2">{{ t('welcome.emailStatUnsub') }}</div>
                            </div>
                        </div>

                        <!-- Automation card -->
                        <div class="rounded-2xl bg-[color:var(--hp-card)] border p-4 md:p-5">
                            <div class="text-[11px] tracking-[0.1em] text-[color:var(--hp-muted)] font-medium uppercase">{{ t('welcome.emailAutoLabel') }}</div>
                            <ul class="mt-4 space-y-2.5 list-none p-0">
                                <li v-for="(auto, i) in (tm('welcome.emailAuto') as any[])" :key="i" class="flex items-center gap-3">
                                    <span v-if="auto.done" class="w-5 h-5 rounded-full bg-[color:var(--hp-ink)] text-[color:var(--hp-cream)] inline-flex items-center justify-center text-[10px]">&check;</span>
                                    <span v-else class="w-5 h-5 rounded-full bg-[color:var(--hp-sand)]"></span>
                                    <span class="text-sm text-[color:var(--hp-ink)] flex-1">{{ rt(auto.t) }}</span>
                                    <span class="text-xs text-[color:var(--hp-muted)]">{{ rt(auto.type) }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Right: copy + feature list -->
                    <div class="col-span-12 lg:col-span-6 lg:pl-6 lg:pt-8 order-1 lg:order-2">
                        <div class="hp-eyebrow">{{ t('welcome.emailEyebrow') }}</div>
                        <h2 class="font-fraunces font-bold text-[24px] md:text-[30px] lg:text-[36px] leading-[1.15] mt-4 max-w-[430px]">{{ t('welcome.emailTitle') }}</h2>
                        <p class="mt-4 md:mt-5 text-[color:var(--hp-muted)] leading-7 max-w-[460px]">
                            {{ t('welcome.emailSub') }}
                        </p>

                        <div class="mt-8 md:mt-10 space-y-5 md:space-y-6">
                            <div v-for="(ef, i) in (tm('welcome.emailFeatures') as any[])" :key="i" class="flex gap-4">
                                <div class="w-8 h-8 rounded-lg bg-[color:var(--hp-card)] border shrink-0 flex items-center justify-center text-[color:var(--hp-ink)]">
                                    <svg v-if="i === 0" aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16v16H4z"/><path d="M4 8h16"/></svg>
                                    <svg v-else-if="i === 1" aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                                    <svg v-else aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                </div>
                                <div>
                                    <div class="text-sm font-medium text-[color:var(--hp-ink)]">{{ rt(ef.t) }}</div>
                                    <div class="text-sm text-[color:var(--hp-muted)] mt-1 max-w-[420px] leading-6">{{ rt(ef.d) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Pricing -->
        <section id="pricing" class="bg-[color:var(--hp-sand)] border-b">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-14 md:py-20 lg:py-24">
                <div class="max-w-[520px]">
                    <div class="hp-eyebrow">{{ t('welcome.pricingEyebrow') }}</div>
                    <h2 class="font-fraunces font-bold text-[32px] md:text-[40px] lg:text-[48px] leading-[1.1] mt-4">{{ t('welcome.pricingTitle') }}</h2>
                    <p class="mt-4 text-[color:var(--hp-muted)]">{{ t('welcome.pricingSub') }}</p>
                </div>

                <div class="mt-10 md:mt-12 grid grid-cols-12 gap-6">
                    <!-- Main card -->
                    <div class="col-span-12 lg:col-span-6 rounded-3xl bg-[color:var(--hp-card)] border p-6 md:p-8 lg:p-10">
                        <div class="text-sm text-[color:var(--hp-muted)]">{{ t('welcome.pricingPerTx') }}</div>
                        <div class="font-fraunces font-bold text-[56px] md:text-[68px] lg:text-[80px] leading-none mt-2 text-[color:var(--hp-accent)]">10%</div>
                        <ul class="mt-6 md:mt-8 space-y-3 list-none p-0">
                            <li v-for="(check, i) in (tm('welcome.pricingChecks') as string[])" :key="i" class="flex items-center gap-3 text-sm">
                                <span class="text-[color:var(--hp-accent-deep)]" aria-hidden="true">&check;</span>{{ rt(check) }}
                            </li>
                        </ul>
                        <Link v-if="canRegister" :href="register()" class="mt-8 md:mt-10 rounded-full bg-[color:var(--hp-ink)] text-[color:var(--hp-cream)] block text-center font-medium text-sm py-3.5 no-underline">{{ t('welcome.pricingCta') }}</Link>
                        <div class="text-xs text-[color:var(--hp-muted)] text-center mt-4">{{ t('welcome.pricingNoCc') }}</div>
                    </div>

                    <!-- Calc examples -->
                    <div class="col-span-12 lg:col-span-6 space-y-4">
                        <div class="text-sm font-medium tracking-[0.1em] text-[color:var(--hp-muted)]">{{ t('welcome.pricingCalcLabel') }}</div>
                        <div v-for="(calc, i) in (tm('welcome.pricingCalcs') as any[])" :key="i" class="rounded-2xl bg-[color:var(--hp-card)] border p-5 md:p-6" :class="{ 'ring-1 ring-[color:var(--hp-accent)]/30': i === (tm('welcome.pricingCalcs') as any[]).length - 1 }">
                            <div class="flex items-baseline justify-between gap-4 md:gap-6">
                                <div>
                                    <div class="text-sm text-[color:var(--hp-ink)] font-medium">{{ rt(calc.t) }}</div>
                                    <div class="text-xs text-[color:var(--hp-muted)] mt-1">{{ rt(calc.fee) }}</div>
                                </div>
                                <div class="font-fraunces font-bold text-[22px] md:text-[28px] shrink-0" :class="i === (tm('welcome.pricingCalcs') as any[]).length - 1 ? 'text-[color:var(--hp-accent)]' : 'text-[color:var(--hp-ink)]'">{{ rt(calc.amount) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Testimonials -->
        <section aria-label="Testimonials" class="border-b">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-14 md:py-20 lg:py-24">
                <div class="hp-eyebrow">{{ t('welcome.testimonialsEyebrow') }}</div>
                <h2 class="font-fraunces font-bold text-[28px] md:text-[36px] lg:text-[48px] leading-[1.1] mt-4">{{ t('welcome.testimonialsTitle') }}</h2>

                <div class="mt-10 md:mt-12 grid grid-cols-12 gap-5 md:gap-6">
                    <article v-for="(q, i) in (tm('welcome.testimonials') as any[])" :key="i" class="col-span-12 md:col-span-4 rounded-3xl bg-[color:var(--hp-card)] border p-6 md:p-7 flex flex-col">
                        <p class="font-fraunces font-bold text-[18px] md:text-[22px] leading-[1.35] text-[color:var(--hp-ink)] flex-1">
                            &ldquo;{{ rt(q.q) }}&rdquo;
                        </p>
                        <div class="mt-6 md:mt-8">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-full bg-[color:var(--hp-sand)] font-fraunces font-bold text-[18px] text-[color:var(--hp-ink)] flex items-center justify-center">{{ rt(q.n).charAt(0) }}</div>
                                <div class="flex-1 min-w-0">
                                    <div class="text-sm font-medium text-[color:var(--hp-ink)]">{{ rt(q.n) }}</div>
                                    <div class="text-xs text-[color:var(--hp-muted)]">{{ rt(q.r) }}</div>
                                </div>
                            </div>
                            <div class="font-fraunces font-bold text-[16px] md:text-[20px] text-[color:var(--hp-accent)] mt-3">{{ rt(q.s) }}</div>
                        </div>
                    </article>
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section class="bg-[color:var(--hp-sand)] border-b">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-14 md:py-20 lg:py-24">
                <div class="grid grid-cols-12 gap-8 md:gap-10">
                    <div class="col-span-12 lg:col-span-4">
                        <div class="hp-eyebrow">{{ t('welcome.faqEyebrow') }}</div>
                        <h2 class="font-fraunces font-bold text-[28px] md:text-[32px] lg:text-[36px] leading-[1.15] mt-4">{{ t('welcome.faqTitle') }}</h2>
                    </div>
                    <div class="col-span-12 lg:col-span-8">
                        <div class="divide-y divide-[color:var(--hp-line)]">
                            <details v-for="(faq, i) in (tm('welcome.faq') as any[])" :key="i" class="group py-4 md:py-5">
                                <summary class="flex items-center justify-between gap-4 md:gap-6">
                                    <span class="text-[15px] md:text-[16px] font-medium text-[color:var(--hp-ink)]">{{ rt(faq.q) }}</span>
                                    <span class="hp-chev text-[color:var(--hp-muted)] shrink-0" aria-hidden="true">&#xFF0B;</span>
                                </summary>
                                <p class="mt-3 text-sm text-[color:var(--hp-muted)] leading-7 max-w-[640px]">{{ rt(faq.a) }}</p>
                            </details>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Final CTA -->
        <section aria-label="Call to action" class="border-b">
            <div class="mx-auto max-w-[1101px] px-5 md:px-6 py-16 md:py-24 lg:py-28 text-center">
                <h2 class="font-fraunces font-bold text-[32px] md:text-[48px] lg:text-[60px] leading-[1.1] max-w-[680px] mx-auto">{{ t('welcome.ctaTitle') }}</h2>
                <p class="mt-5 md:mt-6 text-base md:text-lg text-[color:var(--hp-muted)] max-w-[560px] mx-auto">{{ t('welcome.ctaSub') }}</p>
                <div class="mt-8 md:mt-10 flex justify-center">
                    <Link v-if="canRegister" :href="register()" class="rounded-full bg-[color:var(--hp-ink)] text-[color:var(--hp-cream)] px-7 md:px-8 inline-flex items-center text-sm font-medium hp-arrow no-underline" style="height:52px">{{ t('welcome.ctaButton') }}</Link>
                </div>
                <p class="mt-5 md:mt-6 text-xs text-[color:var(--hp-muted)]">{{ t('welcome.ctaNote') }}</p>
            </div>
        </section>
    </div>
</template>

<style>
html:has(.nv-home) {
    scroll-behavior: smooth;
}

.nv-home {
    --hp-ink: #1a1a18;
    --hp-muted: #6b6b65;
    --hp-partner: #8a8a85;
    --hp-cream: #fafaf7;
    --hp-sand: #f2ede4;
    --hp-line: rgba(26, 26, 24, .10);
    --hp-accent: #f45d22;
    --hp-accent-soft: #fef0e8;
    --hp-accent-deep: #c84a1f;
    --hp-btn-send: #b5411b;
    --hp-card: #ffffff;
    --hp-card-alt: #f8f6f2;
    --hp-statement-bg: #1a1a18;
    --hp-statement-fg: #fafaf7;

    background: var(--hp-cream);
    color: var(--hp-ink);
    -webkit-font-smoothing: antialiased;
}

/* Dark mode */
.dark .nv-home {
    --hp-ink: #ededec;
    --hp-muted: #8c8c89;
    --hp-partner: #8a8a85;
    --hp-cream: #131311;
    --hp-sand: #1f1f1d;
    --hp-line: rgba(237, 237, 236, .12);
    --hp-accent: #f56d38;
    --hp-accent-soft: #2e1a10;
    --hp-accent-deep: #f78a5e;
    --hp-btn-send: #c84a1f;
    --hp-card: #1a1a18;
    --hp-card-alt: #222220;
    --hp-statement-bg: #0a0a08;
    --hp-statement-fg: #ededec;
}

/* Override default border color within homepage */
.nv-home *,
.nv-home *::before,
.nv-home *::after {
    border-color: var(--hp-line);
}

/* Eyebrow label */
.nv-home .hp-eyebrow {
    font-size: 12px;
    letter-spacing: 0.1em;
    color: var(--hp-muted);
}

/* Arrow after CTA text */
.nv-home .hp-arrow::after {
    content: "";
    display: inline-block;
    width: .9em;
    height: .9em;
    margin-left: .55em;
    background: currentColor;
    -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M5 12h14M13 5l7 7-7 7'/></svg>") center/contain no-repeat;
    mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M5 12h14M13 5l7 7-7 7'/></svg>") center/contain no-repeat;
    vertical-align: -2px;
}

/* FAQ details toggle */
.nv-home details > summary {
    list-style: none;
    cursor: pointer;
}
.nv-home details > summary::-webkit-details-marker {
    display: none;
}
.nv-home details[open] .hp-chev {
    transform: rotate(180deg);
}
.nv-home .hp-chev {
    transition: transform .2s ease;
}

/* Focus indicators */
.nv-home a:focus-visible,
.nv-home button:focus-visible,
.nv-home summary:focus-visible {
    outline: 2px solid var(--hp-accent);
    outline-offset: 2px;
    border-radius: 4px;
}
</style>
